export function luhnValid(value: string): boolean {
  let sum = 0;
  let doubleDigit = false;
  for (let index = value.length - 1; index >= 0; index -= 1) {
    const digit = value.charCodeAt(index) - 48;
    if (digit < 0 || digit > 9) return false;
    let adjusted = digit;
    if (doubleDigit) {
      adjusted *= 2;
      if (adjusted > 9) adjusted -= 9;
    }
    sum += adjusted;
    doubleDigit = !doubleDigit;
  }
  return sum % 10 === 0;
}

const IBAN_LENGTHS: Readonly<Record<string, number>> = {
  AD: 24, AE: 23, AL: 28, AT: 20, AZ: 28, BA: 20, BE: 16, BG: 22, BH: 22, BR: 29,
  BY: 28, CH: 21, CR: 22, CY: 28, CZ: 24, DE: 22, DK: 18, DO: 28, EE: 20, EG: 29,
  ES: 24, FI: 18, FO: 18, FR: 27, GB: 22, GE: 22, GI: 23, GL: 18, GR: 27, GT: 28,
  HR: 21, HU: 28, IE: 22, IL: 23, IS: 26, IT: 27, JO: 30, KW: 30, KZ: 20, LB: 28,
  LC: 32, LI: 21, LT: 20, LU: 20, LV: 21, MC: 27, MD: 24, ME: 22, MK: 19, MR: 27,
  MT: 31, MU: 30, NL: 18, NO: 15, PK: 24, PL: 28, PS: 29, PT: 25, QA: 29, RO: 24,
  RS: 22, SA: 24, SC: 31, SE: 24, SI: 19, SK: 24, SM: 27, ST: 25, SV: 28, TL: 23,
  TN: 24, TR: 26, UA: 29, VA: 22, VG: 24, XK: 20,
};

export function ibanLengthForCountry(country: string): number | undefined {
  return IBAN_LENGTHS[country.toUpperCase()];
}

export function ibanValid(value: string): boolean {
  const compact = value.replace(/[\s-]/g, "").toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z\d]+$/.test(compact) || IBAN_LENGTHS[compact.slice(0, 2)] !== compact.length) return false;
  const moved = `${compact.slice(4)}${compact.slice(0, 4)}`;
  let remainder = 0;
  for (const character of moved) {
    const numeric = character >= "A" && character <= "Z" ? String(character.charCodeAt(0) - 55) : character;
    for (const digit of numeric) remainder = (remainder * 10 + (digit.charCodeAt(0) - 48)) % 97;
  }
  return remainder === 1;
}

function validDate(year: number, month: number, day: number): boolean {
  if (year < 1800 || year > 2099 || month < 1 || month > 12 || day < 1) return false;
  return new Date(Date.UTC(year, month, 0)).getUTCDate() >= day;
}

function validTwoDigitDate(year: number, month: number, day: number): boolean {
  return validDate(1900 + year, month, day) || validDate(2000 + year, month, day);
}

export interface SwedishIdentityValidation { valid: boolean; coordination: boolean; validation: string[] }

function ymd(year: number, month: number, day: number): number {
  return year * 10_000 + month * 100 + day;
}

function shortSwedishYear(
  year: number,
  month: number,
  day: number,
  separator: string | undefined,
  referenceDate: Date,
): number | undefined {
  if (separator !== "+") return undefined;
  const cutoffYear = referenceDate.getUTCFullYear() - 100;
  const cutoff = ymd(cutoffYear, referenceDate.getUTCMonth() + 1, referenceDate.getUTCDate());
  let candidate = Math.floor(cutoffYear / 100) * 100 + year;
  if (ymd(candidate, month, day) > cutoff) candidate -= 100;
  return candidate;
}

function parseReferenceDate(referenceDate: string): Date | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(referenceDate)) return undefined;
  const date = new Date(`${referenceDate}T00:00:00.000Z`);
  return Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== referenceDate ? undefined : date;
}

/** Current Stockholm calendar date, with UTC-only fallback for runtimes lacking time-zone data. */
export function currentSwedishReferenceDate(now = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Europe/Stockholm",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(now);
    const part = (type: string) => parts.find((candidate) => candidate.type === type)?.value;
    const year = part("year");
    const month = part("month");
    const day = part("day");
    if (year && month && day) return `${year}-${month}-${day}`;
  } catch {
    // Browser engines with no IANA zone data still get a deterministic UTC date.
  }
  return now.toISOString().slice(0, 10);
}

export function validateSwedishIdentity(value: string, referenceDate?: string): SwedishIdentityValidation {
  const frozenReferenceDate = parseReferenceDate(referenceDate ?? currentSwedishReferenceDate());
  if (!frozenReferenceDate) return { valid: false, coordination: false, validation: [] };
  const separator = value.match(/[-+]/)?.[0];
  const compact = value.replace(/[-+]/g, "");
  if (!/^\d{10}$|^\d{12}$/.test(compact)) return { valid: false, coordination: false, validation: [] };
  const short = compact.slice(-10);
  const year = Number(short.slice(0, 2));
  const month = Number(short.slice(2, 4));
  const encodedDay = Number(short.slice(4, 6));
  const coordination = encodedDay >= 61 && encodedDay <= 91;
  const day = coordination ? encodedDay - 60 : encodedDay;
  const plusYear = compact.length === 10
    ? shortSwedishYear(year, month, day, separator, frozenReferenceDate)
    : undefined;
  const dateOk = compact.length === 12
    ? validDate(Number(compact.slice(0, 4)), month, day)
    : separator === "+"
      ? plusYear !== undefined && validDate(plusYear, month, day)
      : validTwoDigitDate(year, month, day);
  const luhnOk = luhnValid(short);
  return { valid: dateOk && luhnOk, coordination, validation: dateOk && luhnOk ? ["date", "luhn"] : [] };
}

export interface DanishCprValidation { validDate: boolean; mod11: boolean }

export function validateDanishCpr(value: string): DanishCprValidation {
  const compact = value.replace(/[\s-]/g, "");
  if (!/^\d{10}$/.test(compact)) return { validDate: false, mod11: false };
  const day = Number(compact.slice(0, 2));
  const month = Number(compact.slice(2, 4));
  const year = Number(compact.slice(4, 6));
  const valid = validTwoDigitDate(year, month, day);
  const weights = [4, 3, 2, 7, 6, 5, 4, 3, 2, 1];
  const sum = [...compact].reduce((total, character, index) => total + Number(character) * weights[index]!, 0);
  return { validDate: valid, mod11: sum % 11 === 0 };
}

export function validateLithuanianPersonalCode(value: string): boolean {
  if (!/^\d{11}$/.test(value)) return false;
  const century = Number(value[0]);
  const yearPrefix = century <= 2 ? 1800 : century <= 4 ? 1900 : century <= 6 ? 2000 : 0;
  if (yearPrefix === 0 || !validDate(yearPrefix + Number(value.slice(1, 3)), Number(value.slice(3, 5)), Number(value.slice(5, 7)))) return false;
  const digits = [...value].map(Number);
  const firstWeights = [1, 2, 3, 4, 5, 6, 7, 8, 9, 1];
  const secondWeights = [3, 4, 5, 6, 7, 8, 9, 1, 2, 3];
  const check = (weights: number[]) => digits.slice(0, 10).reduce((total, digit, index) => total + digit * weights[index]!, 0) % 11;
  let checksum = check(firstWeights);
  if (checksum === 10) checksum = check(secondWeights);
  if (checksum === 10) checksum = 0;
  return checksum === digits[10];
}
